
import React, { useEffect, useMemo, useState } from "react";

import {
  FaThLarge,
  FaTable,
  FaSearch,
  FaFilter,
} from "react-icons/fa";

const STATUS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const REGLEMENT_STATUS = [
  "",
  "Réglé",
  "Partiellement réglé",
  "En cours",
  "Non réglé",
  "Annulé",
  "Problème",
  "Inexistant",
];

const FiltersBar = ({
  loading = false,

  dojos = [],
  selectedDojo = "",
  onDojoChange,

  groups = [],
  filterGroup = "",
  onGroupChange,

  seasons = [],
  selectedSeason = "",
  onSeasonChange,

  search = "",
  onSearchChange,

  statutInscription = "",
  onStatutChange,

  reglementFilter = "",
  onReglementChange,

  sortBy = "alpha",
  onSortByChange,

  viewMode = "grid",
  onViewModeChange,
}) => {
  const [localSearch, setLocalSearch] = useState(search || "");

  // Synchronisation recherche
  useEffect(() => {
    setLocalSearch(search || "");
  }, [search]);

  // Recherche avec debounce
  useEffect(() => {
    if (localSearch === search) return;

    const timer = setTimeout(() => {
      onSearchChange?.(localSearch);
    }, 250);

    return () => clearTimeout(timer);
  }, [localSearch, search, onSearchChange]);

  const hasSingleDojo = useMemo(
    () => dojos.length === 1,
    [dojos.length]
  );

  const isTable = viewMode === "table";

  const fieldClass = [
    "w-full min-w-0 rounded-xl",
    "border border-slate-300 dark:border-slate-700",
    "bg-white dark:bg-slate-900",
    "px-3 py-2.5",
    "text-sm text-slate-900 dark:text-slate-100",
    "outline-none transition",
    "focus:border-violet-400 focus:ring-2 focus:ring-violet-400/30",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ].join(" ");

  const labelClass =
    "mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300";

  const viewButtonClass = (active) =>
    [
      "inline-flex items-center justify-center gap-2",
      "rounded-xl px-3 py-2.5",
      "text-xs font-bold transition",
      active
        ? "bg-[#013E8E] text-white shadow-sm dark:bg-blue-600"
        : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
    ].join(" ");

  return (
    <section className="space-y-5">

      {/* EN-TÊTE */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
            <FaFilter className="text-[#013E8E] dark:text-blue-400" />
            Filtres adhérents
          </div>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Recherchez vos adhérents par saison, groupe, statut ou règlement.
          </p>
        </div>

        {/* SÉLECTEUR DE VUE */}
        <div className="inline-flex w-full items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1 sm:w-auto dark:border-slate-700 dark:bg-slate-800/60">
          <button
            type="button"
            onClick={() => onViewModeChange?.("grid")}
            aria-pressed={!isTable}
            disabled={loading}
            className={`${viewButtonClass(!isTable)} flex-1 sm:flex-none`}
          >
            <FaThLarge />
            Cartes
          </button>

          <button
            type="button"
            onClick={() => onViewModeChange?.("table")}
            aria-pressed={isTable}
            disabled={loading}
            className={`${viewButtonClass(isTable)} flex-1 sm:flex-none`}
          >
            <FaTable />
            Tableau
          </button>
        </div>
      </div>

      {/* GRILLE DES FILTRES */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">

        {/* RECHERCHE */}
        <div className="min-w-0 sm:col-span-2 lg:col-span-4">
          <label htmlFor="listing-search" className={labelClass}>
            Recherche
          </label>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              id="listing-search"
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              disabled={loading}
              placeholder="Nom ou prénom..."
              className={`${fieldClass} pl-9`}
            />
          </div>
        </div>

        {/* SAISON */}
        <div className="min-w-0 lg:col-span-2">
          <label htmlFor="listing-season" className={labelClass}>
            Saison
          </label>

          <select
            id="listing-season"
            value={selectedSeason}
            onChange={(e) =>
              onSeasonChange?.(e.target.value)
            }
            disabled={loading}
            className={fieldClass}
          >
            <option value="">Toutes les saisons</option>

            {seasons.map((season) => (
              <option key={season} value={season}>
                {season}
              </option>
            ))}
          </select>
        </div>

        {/* DOJO */}
        {!hasSingleDojo && (
          <div className="min-w-0 lg:col-span-2">
            <label htmlFor="listing-dojo" className={labelClass}>
              Dojo
            </label>

            <select
              id="listing-dojo"
              value={selectedDojo}
              onChange={(e) =>
                onDojoChange?.(e.target.value)
              }
              disabled={loading}
              className={fieldClass}
            >
              <option value="">Tous les dojos</option>

              {dojos.map((dojo) => (
                <option
                  key={dojo.id}
                  value={String(dojo.id)}
                >
                  {dojo.dojo_nom ||
                    dojo.nom ||
                    `Dojo #${dojo.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* GROUPE */}
        <div className="min-w-0 lg:col-span-2">
          <label htmlFor="listing-group" className={labelClass}>
            Groupe
          </label>

          <select
            id="listing-group"
            value={filterGroup}
            onChange={(e) =>
              onGroupChange?.(e.target.value)
            }
            disabled={loading}
            className={fieldClass}
          >
            <option value="">Tous les groupes</option>

            {groups.map((group) => (
              <option key={group.id} value={group.nom}>
                {group.nom}
              </option>
            ))}
          </select>
        </div>

        {/* STATUT INSCRIPTION */}
        <div className="min-w-0 lg:col-span-2">
          <label htmlFor="listing-status" className={labelClass}>
            Statut inscription
          </label>

          <select
            id="listing-status"
            value={statutInscription}
            onChange={(e) =>
              onStatutChange?.(e.target.value)
            }
            disabled={loading}
            className={fieldClass}
          >
            {STATUS.map((status) => (
              <option
                key={status || "all"}
                value={status}
              >
                {status || "Tous les statuts"}
              </option>
            ))}
          </select>
        </div>

        {/* REGLEMENT INSCRIPTION */}
        <div className="min-w-0 lg:col-span-3">
          <label htmlFor="listing-reglement" className={labelClass}>
            Règlement inscription
          </label>

          <select
            id="listing-reglement"
            value={reglementFilter}
            onChange={(e) =>
              onReglementChange?.(e.target.value)
            }
            disabled={loading}
            className={fieldClass}
          >
            {REGLEMENT_STATUS.map((status) => (
              <option
                key={status || "all"}
                value={status}
              >
                {status || "Tous les règlements"}
              </option>
            ))}
          </select>
        </div>

        {/* TRI */}
        <div className="min-w-0 lg:col-span-3">
          <label htmlFor="listing-sort" className={labelClass}>
            Trier par
          </label>

          <select
            id="listing-sort"
            value={sortBy}
            onChange={(e) =>
              onSortByChange?.(e.target.value)
            }
            disabled={loading}
            className={fieldClass}
          >
            <option value="alpha">Nom A → Z</option>
            <option value="alpha-desc">Nom Z → A</option>
            <option value="age-asc">Âge croissant</option>
            <option value="age-desc">Âge décroissant</option>
            <option value="statut">Par statut</option>
            <option value="reglement">
              Par règlement (à vérifier en premier)
            </option>
          </select>
        </div>
      </div>

      {/* INFORMATION */}
      {reglementFilter && (
        <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
          <FaFilter className="shrink-0" />

          <span>
            Filtre actif : règlements « {reglementFilter} »
          </span>

          <button
            type="button"
            onClick={() => onReglementChange?.("")}
            className="ml-auto shrink-0 rounded-lg px-2 py-1 font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40"
          >
            Effacer
          </button>
        </div>
      )}
    </section>
  );
};

export default FiltersBar;
